import { Icon } from '@iconify/react/dist/iconify.js';
import { Loader, Tooltip } from '@mantine/core';
import type { GroupMember, PermissionGroup } from '../../../../api/rbac-client/rbac-client.type';
import { newCOLORS } from '../../../../styles/colors';
import { inkLight } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Text } from '../../../../ui-library/typography/typography';
import { MembersTable } from './groups-tab.helpers';
import {
  EmptyStateText,
  GroupSection,
  GroupSectionHeader,
  GroupSectionTitle,
  GroupsContentContainer,
  GroupsContentHeader,
  GroupsContentHeaderActions,
} from './groups-tab.styles';

export function GroupsContent({
  groups,
  selectedGroupId,
  membersByGroupId,
  isLoading,
  onOpenAddUser,
  onOpenEditGroups,
}: {
  groups: PermissionGroup[];
  selectedGroupId: string | null;
  membersByGroupId: Record<string, GroupMember[]>;
  isLoading: boolean;
  onOpenAddUser: () => void;
  onOpenEditGroups: () => void;
}) {
  const selectedGroup = selectedGroupId ? groups.find((g) => g.id === selectedGroupId) : null;
  const title = selectedGroup ? selectedGroup.group_type_display : 'All Groups';
  const displayGroups = selectedGroup ? [selectedGroup] : groups;

  return (
    <GroupsContentContainer>
      <GroupsContentHeader>
        <Text weight="bold" size="large">
          {title}
        </Text>
        <GroupsContentHeaderActions>
          <Button
            variant="outline"
            size="xs"
            radius="xl"
            onClick={onOpenEditGroups}
            leftSection={<Icon icon="material-symbols:edit" width={16} />}
          >
            Edit Groups
          </Button>
          <Button
            variant="primary"
            size="xs"
            radius="xl"
            onClick={onOpenAddUser}
            leftSection={<Icon icon="material-symbols:add-circle-outline" width={16} color={newCOLORS.white} />}
          >
            Add Users
          </Button>
        </GroupsContentHeaderActions>
      </GroupsContentHeader>

      {isLoading ? (
        <EmptyStateText>
          <Loader size="sm" />
        </EmptyStateText>
      ) : displayGroups.length === 0 ? (
        <EmptyStateText>
          No groups found. Groups will appear here once users are assigned to permission groups.
        </EmptyStateText>
      ) : (
        displayGroups.map((group) => (
          <GroupSection key={group.id}>
            {!selectedGroup && (
              <GroupSectionHeader>
                <GroupSectionTitle>{group.group_type_display}</GroupSectionTitle>
                <Tooltip label={group.description} withArrow disabled={!group.description}>
                  <span tabIndex={0} style={{ display: 'inline-flex', cursor: 'pointer' }}>
                    <Icon icon="material-symbols:info-outline" width={16} color={inkLight} />
                  </span>
                </Tooltip>
              </GroupSectionHeader>
            )}
            <MembersTable members={membersByGroupId[group.id] ?? []} />
          </GroupSection>
        ))
      )}
    </GroupsContentContainer>
  );
}
